import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import { createNotification } from "@/lib/createNotification";
import { calculateContractBalance } from "@/lib/contractBalance";
import {
  getContractAddons,
  activateAddonFull,
  updateAddonStatus,
  getAddonMeta,
  ADDON_CATALOG,
  CATEGORY_META,
  type ContractAddon,
  type ActivationMode,
  type AddonStatus,
  type AddonCatalogItem,
} from "@/lib/contractAddons";

interface ContractInfo {
  id: string;
  client_id: string;
  client_name: string;
  tenant_id: string;
  event_date?: string;
  event_location?: string;
  title?: string;
}

interface Props {
  contract: ContractInfo;
  tenantId: string;
}

/* ── Status / Mode display maps ── */
const STATUS_MAP: Record<AddonStatus, { label: string; dot: string; badge: string }> = {
  active:    { label: "Activo",      dot: "bg-emerald-400", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  disabled:  { label: "Desactivado", dot: "bg-slate-300",   badge: "bg-slate-100 text-slate-500 border-slate-200" },
  cancelled: { label: "Cancelado",   dot: "bg-red-400",     badge: "bg-red-50 text-red-500 border-red-200" },
};

const MODE_MAP: Record<ActivationMode, { label: string; badge: string }> = {
  paid:  { label: "Cobrado", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  gift:  { label: "Regalo",  badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  promo: { label: "Promo",   badge: "bg-violet-50 text-violet-600 border-violet-200" },
};

/* ── Modal state ── */
interface ModalState {
  open: boolean;
  step: "select" | "configure";
  selectedKey: string;
  mode: ActivationMode;
  price: number;
  promoCode: string;
  notes: string;
}

const BLANK_MODAL: ModalState = {
  open:        false,
  step:        "select",
  selectedKey: "",
  mode:        "paid",
  price:       0,
  promoCode:   "",
  notes:       "",
};

export default function AddOnsTab({ contract, tenantId }: Props) {
  const [addons, setAddons]         = useState<ContractAddon[]>([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [modal, setModal]           = useState<ModalState>(BLANK_MODAL);
  const [moduleMap, setModuleMap]   = useState<Record<string, boolean>>({});
  const [clientMap, setClientMap]   = useState<Record<string, boolean>>({});
  const [statusAction, setStatusAction] = useState<string | null>(null);

  /* ── Fetch addons + module gates ── */
  const fetchAll = useCallback(async () => {
    setLoading(true);

    const [addonsData, modulesRes] = await Promise.all([
      getContractAddons(tenantId, contract.id),
      supabase
        .from("tenant_modules")
        .select("module_id, is_enabled")
        .eq("tenant_id", tenantId),
    ]);

    setAddons(addonsData);

    // Build module key → enabled map
    const saasModRes = await supabase
      .from("saas_modules")
      .select("id, module_key");

    const saasMap: Record<string, string> = {};
    (saasModRes.data ?? []).forEach((m: { id: string; module_key: string }) => {
      saasMap[m.module_key] = m.id;
    });

    const enabledIds = new Set(
      (modulesRes.data ?? [])
        .filter((tm: { is_enabled: boolean }) => tm.is_enabled)
        .map((tm: { module_id: string }) => tm.module_id),
    );

    const mMap: Record<string, boolean> = {};
    ADDON_CATALOG.forEach((a) => {
      if (!a.moduleKey) {
        mMap[a.key] = true; // no gate
      } else {
        const mid = saasMap[a.moduleKey];
        mMap[a.key] = mid ? enabledIds.has(mid) : false;
      }
    });
    setModuleMap(mMap);

    // Check client-level duplicates (info only)
    const clientChecks = await Promise.all(
      ADDON_CATALOG.map(async (a) => {
        const { data } = await supabase
          .from("contract_addons")
          .select("id")
          .eq("tenant_id", tenantId)
          .eq("client_id", contract.client_id)
          .eq("addon_key", a.key)
          .eq("status", "active")
          .neq("contract_id", contract.id)
          .maybeSingle();
        return { key: a.key, has: !!data };
      }),
    );
    const cMap: Record<string, boolean> = {};
    clientChecks.forEach((c) => { cMap[c.key] = c.has; });
    setClientMap(cMap);

    setLoading(false);
  }, [tenantId, contract.id, contract.client_id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ── Derived ── */
  const activatedKeys = new Set(addons.map((a) => a.addon_key));
  const availableAddons = ADDON_CATALOG.filter((a) => !activatedKeys.has(a.key));

  const categorized = Object.entries(CATEGORY_META).map(([cat, catMeta]) => ({
    cat,
    catMeta,
    items: availableAddons.filter((a) => a.category === cat),
  })).filter((g) => g.items.length > 0);

  const totalPaid = addons
    .filter((a) => a.activation_mode === "paid" && a.status !== "cancelled")
    .reduce((s, a) => s + Number(a.price), 0);
  const activeCount = addons.filter((a) => a.status === "active").length;

  /* ── Modal helpers ── */
  const openModal = () => setModal({ ...BLANK_MODAL, open: true });

  const selectAddon = (addon: AddonCatalogItem) => {
    setModal((m) => ({
      ...m,
      step:        "configure",
      selectedKey: addon.key,
      price:       addon.defaultPrice,
      mode:        addon.defaultPrice > 0 ? "paid" : "gift",
    }));
  };

  const closeModal = () => setModal(BLANK_MODAL);

  const handleActivate = async () => {
    if (!modal.selectedKey) return;
    setSaving(true);
    const { success, error } = await activateAddonFull({
      tenantId,
      contractId:  contract.id,
      clientId:    contract.client_id,
      clientName:  contract.client_name,
      addonKey:    modal.selectedKey,
      mode:        modal.mode,
      price:       modal.price,
      promoCode:   modal.promoCode,
      notes:       modal.notes || undefined,
      contractMeta: {
        event_date:     contract.event_date,
        event_location: contract.event_location,
        title:          contract.title,
      },
    });

    if (success || error === "addon_already_exists") {
      closeModal();
      await fetchAll();
      const addonLabel = ADDON_CATALOG.find((a) => a.key === modal.selectedKey)?.label ?? modal.selectedKey;
      createNotification({
        tenantId,
        type: 'success',
        category: 'addon',
        title: 'Add-on activated',
        message: `"${addonLabel}" has been activated for ${contract.client_name} on contract ${contract.contract_number ?? contract.id.slice(0, 8)}.`,
        entityType: 'contract',
        entityId: contract.id,
      });
    }
    setSaving(false);
  };

  const handleStatusChange = async (addonId: string, status: AddonStatus, addonLabel?: string) => {
    // Confirm cancellation
    if (status === "cancelled") {
      const result = await Swal.fire({
        title: "Cancel this add-on?",
        html: `<p style="color:#64748b;font-size:14px;">"${addonLabel ?? "Add-on"}" will be cancelled and excluded from the contract balance.</p>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Cancel Add-on",
        cancelButtonText: "Keep",
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#94a3b8",
        background: "#fff",
        color: "#0f172a",
      });
      if (!result.isConfirmed) return;
    }
    setStatusAction(addonId);
    await updateAddonStatus(tenantId, addonId, status);
    // Recalculate balance after status change (charges may have changed)
    await calculateContractBalance(tenantId, contract.id, { sync: true });
    await fetchAll();
    setStatusAction(null);
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });

  const selectedCatalog = ADDON_CATALOG.find((a) => a.key === modal.selectedKey);

  return (
    <div className="space-y-7">

      {/* ── Header stats ── */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-1.5">Add-ons activos</p>
          <p className="text-[#0f172a] text-2xl font-bold">{activeCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-1.5">Cargos por add-ons</p>
          <p className="text-[#C8A96E] text-2xl font-bold">${totalPaid.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-1.5">Disponibles</p>
          <p className="text-slate-500 text-2xl font-bold">{availableAddons.length}</p>
        </div>
      </div>

      {/* ── Active add-ons table ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-puzzle-fill text-[#C8A96E]"></i>
            </span>
            Add-ons de este contrato
          </h3>
          <button
            onClick={openModal}
            className="flex items-center gap-1.5 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-xs font-semibold px-3.5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Activar Add-on
          </button>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : addons.length === 0 ? (
          <div className="bg-slate-50 rounded-xl border border-dashed border-slate-300 p-8 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white border border-slate-200 mx-auto mb-3">
              <i className="ri-puzzle-line text-slate-300 text-xl"></i>
            </div>
            <p className="text-slate-500 text-sm font-medium mb-1">Sin add-ons activados</p>
            <p className="text-slate-400 text-xs mb-4">Este contrato no tiene extras activados. Haz clic en &quot;Activar Add-on&quot; para monetizarlo.</p>
            <button
              onClick={openModal}
              className="inline-flex items-center gap-1.5 bg-[#C8A96E]/10 hover:bg-[#C8A96E]/20 text-[#C8A96E] text-xs font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-circle-line"></i>
              Activar primer add-on
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Add-on</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Modo</th>
                  <th className="text-right text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Precio</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Estado</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Fecha</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">Notas</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {addons.map((addon) => {
                  const meta       = getAddonMeta(addon.addon_key);
                  const statusInfo = STATUS_MAP[addon.status] ?? STATUS_MAP.active;
                  const modeInfo   = MODE_MAP[addon.activation_mode] ?? MODE_MAP.paid;
                  const isLoading  = statusAction === addon.id;

                  return (
                    <tr key={addon.id} className="hover:bg-slate-50 transition-colors">
                      {/* Addon name */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${meta.bg}`}>
                            <i className={`${meta.icon} ${meta.color} text-xs`}></i>
                          </div>
                          <div>
                            <p className="text-[#0f172a] text-sm font-medium">{meta.label}</p>
                            {addon.promo_code && (
                              <p className="text-violet-500 text-[10px] font-mono">{addon.promo_code}</p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Mode */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${modeInfo.badge}`}>
                          {modeInfo.label}
                        </span>
                      </td>

                      {/* Price */}
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-semibold ${addon.activation_mode === "paid" ? "text-[#C8A96E]" : "text-emerald-500"}`}>
                          {addon.activation_mode === "paid" ? `$${Number(addon.price).toFixed(0)}` : "—"}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusInfo.badge}`}>
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusInfo.dot}`}></span>
                          {statusInfo.label}
                        </span>
                      </td>

                      {/* Date */}
                      <td className="px-4 py-3">
                        <p className="text-slate-400 text-xs">{formatDate(addon.created_at)}</p>
                      </td>

                      {/* Notes */}
                      <td className="px-4 py-3 max-w-[160px]">
                        <p className="text-slate-400 text-xs truncate">{addon.notes ?? "—"}</p>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          {isLoading ? (
                            <span className="w-6 h-6 flex items-center justify-center">
                              <i className="ri-loader-4-line animate-spin text-slate-400 text-xs"></i>
                            </span>
                          ) : (
                            <>
                              {addon.status === "active" && (
                                <button
                                  onClick={() => handleStatusChange(addon.id, "disabled", meta.label)}
                                  title="Desactivar temporalmente"
                                  className="w-7 h-7 flex items-center justify-center text-slate-300 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all cursor-pointer"
                                >
                                  <i className="ri-eye-off-line text-xs"></i>
                                </button>
                              )}
                              {addon.status === "disabled" && (
                                <button
                                  onClick={() => handleStatusChange(addon.id, "active", meta.label)}
                                  title="Reactivar"
                                  className="w-7 h-7 flex items-center justify-center text-slate-300 hover:text-emerald-500 hover:bg-emerald-50 rounded-lg transition-all cursor-pointer"
                                >
                                  <i className="ri-checkbox-circle-line text-xs"></i>
                                </button>
                              )}
                              {addon.status !== "cancelled" && (
                                <button
                                  onClick={() => handleStatusChange(addon.id, "cancelled", meta.label)}
                                  title="Cancelar add-on"
                                  className="w-7 h-7 flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
                                >
                                  <i className="ri-close-circle-line text-xs"></i>
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Footer total */}
            {totalPaid > 0 && (
              <div className="border-t border-slate-100 px-4 py-3 flex items-center justify-between bg-slate-50">
                <span className="text-slate-500 text-xs font-medium">Total cargos por add-ons</span>
                <span className="text-[#C8A96E] font-bold text-sm">${totalPaid.toLocaleString()}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Available add-ons by category ── */}
      {availableAddons.length > 0 && (
        <div>
          <h3 className="text-[#0f172a] font-semibold text-sm mb-4 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-store-3-line text-slate-400"></i>
            </span>
            Catálogo de extras disponibles
          </h3>

          <div className="space-y-5">
            {categorized.map(({ cat, catMeta, items }) => (
              <div key={cat}>
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                  <i className={`${catMeta.icon} text-xs`}></i>
                  {catMeta.label}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {items.map((addon) => {
                    const isEnabled = moduleMap[addon.key] !== false;
                    const otherContract = clientMap[addon.key] === true;

                    return (
                      <div
                        key={addon.key}
                        className={`bg-white rounded-xl border p-4 flex items-start gap-3 transition-all ${
                          isEnabled
                            ? `${addon.accentBorder} hover:shadow-sm`
                            : "border-slate-200 opacity-60"
                        }`}
                      >
                        <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${addon.bg}`}>
                          <i className={`${addon.icon} ${addon.color} text-base`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-[#0f172a] text-sm font-semibold">{addon.label}</p>
                            {addon.defaultPrice > 0 && (
                              <span className="text-[10px] font-medium text-slate-400">
                                desde ${addon.defaultPrice}
                              </span>
                            )}
                          </div>
                          <p className="text-slate-400 text-xs mt-0.5 leading-relaxed">{addon.description}</p>
                          {otherContract && (
                            <p className="text-amber-600 text-xs mt-1 flex items-center gap-1">
                              <i className="ri-information-line"></i>
                              Activo en otro contrato de este cliente
                            </p>
                          )}
                          {!isEnabled && (
                            <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                              <i className="ri-lock-line"></i>
                              Módulo no habilitado
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            if (!isEnabled) return;
                            setModal({ ...BLANK_MODAL, open: true, step: "configure", selectedKey: addon.key, price: addon.defaultPrice, mode: addon.defaultPrice > 0 ? "paid" : "gift" });
                          }}
                          disabled={!isEnabled}
                          className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${
                            isEnabled
                              ? `${addon.bg} ${addon.color} hover:opacity-80 cursor-pointer`
                              : "bg-slate-100 text-slate-300 cursor-not-allowed"
                          }`}
                        >
                          <i className="ri-add-line"></i>
                          Activar
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════
          MODAL — Activate Add-on
      ════════════════════════════════════════ */}
      {modal.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh]">

            {/* Modal header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 flex-shrink-0">
              <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-[#C8A96E]/10">
                <i className="ri-puzzle-line text-[#C8A96E] text-sm"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#0f172a] font-semibold text-sm">
                  {modal.step === "select" ? "Seleccionar Add-on" : "Configurar Activación"}
                </p>
                <p className="text-slate-400 text-xs">{contract.client_name}</p>
              </div>
              {modal.step === "configure" && (
                <button
                  onClick={() => setModal((m) => ({ ...m, step: "select", selectedKey: "" }))}
                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap mr-1"
                >
                  <i className="ri-arrow-left-line"></i>
                  Volver
                </button>
              )}
              <button
                onClick={closeModal}
                className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg cursor-pointer transition-all"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* ── STEP 1: Select add-on ── */}
            {modal.step === "select" && (
              <div className="p-5 overflow-y-auto flex-1 space-y-4">
                {Object.entries(CATEGORY_META).map(([cat, catMeta]) => {
                  const items = ADDON_CATALOG.filter(
                    (a) => a.category === cat && !activatedKeys.has(a.key),
                  );
                  if (items.length === 0) return null;
                  return (
                    <div key={cat}>
                      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                        <i className={`${catMeta.icon} text-xs`}></i>
                        {catMeta.label}
                      </p>
                      <div className="space-y-2">
                        {items.map((addon) => {
                          const isEnabled = moduleMap[addon.key] !== false;
                          return (
                            <button
                              key={addon.key}
                              onClick={() => isEnabled && selectAddon(addon)}
                              disabled={!isEnabled}
                              className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                                isEnabled
                                  ? `${addon.accentBorder} hover:bg-slate-50 cursor-pointer`
                                  : "border-slate-200 opacity-50 cursor-not-allowed"
                              }`}
                            >
                              <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${addon.bg}`}>
                                <i className={`${addon.icon} ${addon.color} text-base`}></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-[#0f172a] text-sm font-semibold">{addon.label}</p>
                                  {!isEnabled && (
                                    <span className="text-[10px] text-red-400 flex items-center gap-0.5">
                                      <i className="ri-lock-line"></i> No habilitado
                                    </span>
                                  )}
                                  {addon.defaultPrice > 0 && isEnabled && (
                                    <span className="text-[10px] text-slate-400 ml-auto">desde ${addon.defaultPrice}</span>
                                  )}
                                </div>
                                <p className="text-slate-400 text-xs">{addon.description}</p>
                              </div>
                              {isEnabled && (
                                <i className="ri-arrow-right-s-line text-slate-300 flex-shrink-0"></i>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {availableAddons.length === 0 && (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    Todos los add-ons disponibles ya están activados en este contrato.
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 2: Configure ── */}
            {modal.step === "configure" && selectedCatalog && (
              <div className="p-5 overflow-y-auto flex-1 space-y-5">

                {/* Selected addon preview */}
                <div className={`flex items-center gap-3 p-3 rounded-xl border ${selectedCatalog.accentBorder} ${selectedCatalog.bg}`}>
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl bg-white/70 flex-shrink-0`}>
                    <i className={`${selectedCatalog.icon} ${selectedCatalog.color} text-base`}></i>
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${selectedCatalog.color}`}>{selectedCatalog.label}</p>
                    <p className="text-slate-500 text-xs">{selectedCatalog.description}</p>
                  </div>
                </div>

                {/* Mode selector */}
                <div>
                  <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">Tipo de activación *</p>
                  <div className="grid grid-cols-3 gap-2">
                    {(
                      [
                        { id: "paid",  label: "Cobrado",  icon: "ri-money-dollar-circle-line", sub: "Cargo al contrato" },
                        { id: "gift",  label: "Regalo",   icon: "ri-gift-line",                sub: "Sin costo" },
                        { id: "promo", label: "Promo",    icon: "ri-coupon-3-line",            sub: "Código promocional" },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setModal((m) => ({ ...m, mode: opt.id }))}
                        className={`flex flex-col items-center gap-1 py-3 px-2 rounded-xl border text-xs font-medium transition-all cursor-pointer ${
                          modal.mode === opt.id
                            ? "border-[#C8A96E] bg-[#C8A96E]/5 text-[#C8A96E]"
                            : "border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600"
                        }`}
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${opt.icon} text-base`}></i>
                        </span>
                        <span className="font-semibold">{opt.label}</span>
                        <span className={`text-[10px] ${modal.mode === opt.id ? "text-[#C8A96E]/70" : "text-slate-300"}`}>
                          {opt.sub}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price — paid only */}
                {modal.mode === "paid" && (
                  <div>
                    <label className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5 block">
                      Precio (USD) *
                    </label>
                    <div className="flex items-center gap-2 max-w-[180px]">
                      <span className="text-slate-400 font-medium">$</span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={modal.price}
                        onChange={(e) => setModal((m) => ({ ...m, price: Number(e.target.value) }))}
                        className="flex-1 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/60 transition-colors"
                      />
                    </div>
                    <p className="text-slate-400 text-xs mt-1.5 flex items-center gap-1">
                      <i className="ri-information-line"></i>
                      Se creará un cargo automático en los cargos del contrato.
                    </p>
                  </div>
                )}

                {/* Promo code */}
                {modal.mode === "promo" && (
                  <div>
                    <label className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5 block">
                      Código Promocional *
                    </label>
                    <input
                      type="text"
                      value={modal.promoCode}
                      onChange={(e) => setModal((m) => ({ ...m, promoCode: e.target.value.toUpperCase() }))}
                      placeholder="PROMO2025"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 transition-colors font-mono uppercase"
                    />
                  </div>
                )}

                {/* Gift note */}
                {modal.mode === "gift" && (
                  <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5 text-xs text-emerald-700">
                    <i className="ri-gift-line mt-0.5 shrink-0 text-base"></i>
                    <span>El add-on será activado <strong>sin costo</strong> y registrado como regalo en el contrato.</span>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5 block">
                    Notas internas <span className="text-slate-300 font-normal normal-case">(opcional)</span>
                  </label>
                  <textarea
                    value={modal.notes}
                    onChange={(e) => setModal((m) => ({ ...m, notes: e.target.value }))}
                    placeholder="Observaciones, acuerdos especiales, etc."
                    rows={2}
                    maxLength={300}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 transition-colors resize-none"
                  />
                  <p className="text-slate-300 text-xs text-right">{modal.notes.length}/300</p>
                </div>

                {/* Summary */}
                <div className="flex items-start gap-2 bg-[#C8A96E]/5 border border-[#C8A96E]/20 rounded-xl px-3.5 py-3">
                  <i className="ri-checkbox-circle-fill text-[#C8A96E] text-base shrink-0 mt-0.5"></i>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    {modal.mode === "paid" && `Se activará "${selectedCatalog.label}" y se creará un cargo de $${modal.price} en los cargos del contrato.`}
                    {modal.mode === "gift" && `"${selectedCatalog.label}" será activado como regalo sin cargo adicional.`}
                    {modal.mode === "promo" && `"${selectedCatalog.label}" será activado con código "${modal.promoCode || "…"}" sin cargo adicional.`}
                  </p>
                </div>
              </div>
            )}

            {/* Modal footer */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-slate-200 flex-shrink-0 bg-slate-50/50">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg bg-white transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              {modal.step === "configure" && (
                <button
                  onClick={handleActivate}
                  disabled={
                    saving ||
                    !modal.selectedKey ||
                    (modal.mode === "promo" && !modal.promoCode.trim())
                  }
                  className="flex items-center gap-2 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  {saving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Activando…
                    </>
                  ) : (
                    <>
                      <i className="ri-checkbox-circle-line"></i>
                      Confirmar activación
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}